import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Users, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { Child } from "@/hooks/useParentData";

interface Props {
  children: Child[];
  onChange: () => void;
}

export default function ChildrenManager({ children, onChange }: Props) {
  const { user } = useAuth();
  const [showAddChild, setShowAddChild] = useState(false);
  const [childToRemove, setChildToRemove] = useState<{ id: string; name: string } | null>(null);
  const [newChild, setNewChild] = useState({ student_name: "", student_email: "", grade: "" });

  const addChild = async () => {
    if (!user?.id || !newChild.student_name || !newChild.student_email) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      const { data: parentData } = await supabase
        .from("parents")
        .select("id, first_name, last_name")
        .eq("user_id", user.id)
        .single();
      if (!parentData) throw new Error("Parent profile not found");

      const { data: invitationData, error: invitationError } = await supabase
        .from("parent_invitations")
        .insert({
          parent_id: parentData.id,
          student_name: newChild.student_name,
          student_email: newChild.student_email,
          grade: newChild.grade || null,
        })
        .select()
        .single();

      if (invitationError) {
        if (invitationError.code === "23505") {
          toast.error("An invitation has already been sent to this student");
        } else {
          throw invitationError;
        }
        return;
      }

      try {
        await supabase.functions.invoke("send-parent-invitation", {
          body: {
            invitationId: invitationData.id,
            studentEmail: newChild.student_email,
            studentName: newChild.student_name,
            parentName: `${parentData.first_name} ${parentData.last_name}`.trim(),
            grade: newChild.grade,
          },
        });
        toast.success("Invitation sent! The student will receive an email.");
      } catch (e) {
        toast.warning("Invitation created but email notification failed");
      }

      setNewChild({ student_name: "", student_email: "", grade: "" });
      setShowAddChild(false);
      onChange();
    } catch (error: any) {
      toast.error(error.message || "Failed to send invitation");
    }
  };

  const removeChild = async () => {
    if (!childToRemove) return;
    try {
      const { error } = await supabase.from("children").delete().eq("id", childToRemove.id);
      if (error) throw error;
      toast.success("Child removed from your account");
      onChange();
    } catch {
      toast.error("Failed to remove child");
    } finally {
      setChildToRemove(null);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>My Children</CardTitle>
              <CardDescription>Manage your children's information</CardDescription>
            </div>
            <Dialog open={showAddChild} onOpenChange={setShowAddChild}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Child
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Child</DialogTitle>
                  <DialogDescription>
                    Add your child's information to monitor their tutoring sessions
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="child-name">Child's Name *</Label>
                    <Input
                      id="child-name"
                      value={newChild.student_name}
                      onChange={(e) => setNewChild({ ...newChild, student_name: e.target.value })}
                      placeholder="Enter child's full name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="child-email">Child's Email *</Label>
                    <Input
                      id="child-email"
                      type="email"
                      value={newChild.student_email}
                      onChange={(e) => setNewChild({ ...newChild, student_email: e.target.value })}
                      placeholder="Enter child's email address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="child-grade">Grade</Label>
                    <Select value={newChild.grade} onValueChange={(v) => setNewChild({ ...newChild, grade: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"].map((g) => (
                          <SelectItem key={g} value={g}>
                            Grade {g}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={addChild} className="flex-1">
                      Add Child
                    </Button>
                    <Button variant="outline" onClick={() => setShowAddChild(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {children.length > 0 ? (
            <div className="space-y-4">
              {children.map((child) => (
                <div key={child.id} className="flex items-center justify-between p-4 border rounded-lg bg-card">
                  <div>
                    <h3 className="font-medium">{child.student_name}</h3>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>{child.student_email}</span>
                      {child.grade && <Badge variant="outline">Grade {child.grade}</Badge>}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setChildToRemove({ id: child.id, name: child.student_name })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No children added yet</p>
              <Button onClick={() => setShowAddChild(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Child
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!childToRemove} onOpenChange={(open) => !open && setChildToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Child</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{childToRemove?.name}</strong> from your account? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={removeChild}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
